import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "src/context/AuthContext";
import axios from "src/common/axiosInstance";
import { request, failureText } from "../Automation/automationApi";
import {
  GitBranch,
  ShoppingBag,
  Brain,
  Bot,
  MessagesSquare,
  FileText,
  Inbox,
  Package,
} from "lucide-react";

// Bot types, grouped the way the main app's Agents page describes them:
// static flows are "Guided chat"; the other seven answer in their own words ("AI chat").
// `character` is the type's default look (presentation only; see src/stage/characters.js).
export const botTypes = {
  static_flow: {
    label: "Static flow",
    method: "guided_chat",
    character: "nila",
    summary: "Guide each conversation with buttons and branches.",
    icon: GitBranch,
    tone: "sky",
    description:
      "Buttons, branches and approved messages. Optional AI interprets replies.",
    path: "/static-flow/new",
    tag: "Fixed conversation",
  },
  sales: {
    label: "Sales assistant",
    method: "ai_chat",
    character: "remy",
    summary: "Answer questions. Qualify leads. Take the next step.",
    icon: ShoppingBag,
    tone: "teal",
    description: "Answer questions, qualify leads and remember what matters.",
    path: "/sales-bots/new",
    tag: "Sales & follow-up",
  },
  memory: {
    label: "Memory assistant",
    method: "ai_chat",
    character: "maya",
    summary: "Remember details for more personal conversations.",
    icon: Brain,
    tone: "sky",
    description: "A conversational role with approved shared contact memory.",
    path: "/memory-bots/new",
    tag: "Flexible conversation",
  },
  general: {
    label: "General assistant",
    method: "ai_chat",
    character: "appy",
    summary: "Give it a role. Let your instructions lead.",
    icon: Bot,
    tone: "slate",
    description: "A focused assistant guided by your business instructions.",
    tag: "Prompt-based",
  },
  faq: {
    label: "FAQ bot",
    method: "ai_chat",
    character: "tara",
    summary: "Instant replies from your approved answers.",
    icon: MessagesSquare,
    tone: "sky",
    description: "Respond using a published set of approved answers.",
    tag: "Approved answers",
  },
  knowledge: {
    label: "Knowledge bot",
    method: "ai_chat",
    character: "atlas",
    summary: "Turn your business documents into helpful answers.",
    icon: FileText,
    tone: "teal",
    description: "Answer questions using a saved business document.",
    tag: "Document Q&A",
  },
  recommendation: {
    label: "Product bot",
    method: "ai_chat",
    character: "hari",
    summary: "Help customers find their best product match.",
    icon: Package,
    tone: "teal",
    description: "Match customers to products in a saved catalogue.",
    tag: "Product matching",
  },
  intake: {
    label: "Intake bot",
    method: "ai_chat",
    character: "kiran",
    summary: "Collect the right details, one question at a time.",
    icon: Inbox,
    tone: "amber",
    description: "Collect the details your team needs from an enquiry.",
    tag: "Information collection",
  },
};

// The same build methods and words as auto-appy src/features/Workspace/agentsCatalog.js.
export const botMethods = {
  guided_chat: {
    label: "Guided chat",
    editor: "Static flow editor",
    use: "Customers follow your questions and buttons.",
    example: "Ask 4 questions, then offer meeting slots",
  },
  ai_chat: {
    label: "AI chat",
    editor: "Bot settings and test chat",
    use: "Answers in its own words from your sources.",
    example: "FAQ, Knowledge, Sales, Memory, Product, Intake or General",
  },
};
// Personal training content. `plain`, `bestFor` and `format` explain each kind to a beginner.
export const sourceTypes = {
  faq: {
    botType: "faq",
    ...botTypes.faq,
    label: "FAQ answers",
    path: "/faq-bots",
    plain: "Questions with the exact answers you approve.",
    bestFor: "Timings, prices, delivery, refund policy",
    format: "Type or paste questions and answers",
    usedBy: ["faq"],
  },
  knowledge: {
    botType: "knowledge",
    ...botTypes.knowledge,
    label: "Documents",
    path: "/knowledge-bots",
    plain: "A PDF your bot reads, then answers in its own words.",
    bestFor: "Brochures, service guides, handbooks",
    format: "Upload a PDF",
    usedBy: ["knowledge", "sales"],
  },
  product: {
    botType: "recommendation",
    ...botTypes.recommendation,
    label: "Product catalogues",
    path: "/product-bots",
    plain: "A sheet of what you sell, so a bot can suggest the right item.",
    bestFor: "Price lists, property listings, course lists",
    format: "Upload a CSV or Excel sheet",
    usedBy: ["recommendation", "sales"],
  },
  intake: {
    botType: "intake",
    ...botTypes.intake,
    label: "Intake forms",
    path: "/intake-bots",
    plain: "The details to collect from each enquiry, asked one at a time.",
    bestFor: "Lead qualification, service requests, bookings",
    format: "List the questions, add a policy PDF",
    usedBy: ["intake", "sales"],
  },
};
export const workspaceUrl = (path, tenant, extra = {}) => {
  const p = new URLSearchParams({
    ...extra,
    ...(tenant ? { workspace: tenant } : {}),
  });
  return path + (p.toString() ? "?" + p.toString() : "");
};
// Bots, channels and history belong to organisations the person owns or administers.
export const managedOrganisations = (user) =>
  (user?.workspaces || []).filter((w) => ["owner", "admin"].includes(w.role));

// The requested organisation, then the current one, then the only one. Never an
// arbitrary first organisation, and never an ID the person cannot manage.
export function resolveTenant(user, requested) {
  const organisations = managedOrganisations(user);
  const candidate =
    requested ||
    user?.current_workspace?.id ||
    (organisations.length === 1 ? organisations[0].id : "");
  return organisations.some((w) => w.id === candidate) ? candidate : "";
}

export function useWorkspace() {
  const { currentUser } = useContext(AuthContext);
  const [params, setParams] = useSearchParams();
  const organisations = managedOrganisations(currentUser);
  const tenant = resolveTenant(currentUser, params.get("workspace"));
  const choose = (id) => {
    const next = new URLSearchParams(params);
    next.set("workspace", id);
    next.delete("page");
    next.delete("bot");
    setParams(next);
  };
  return { tenant, organisations, choose, currentUser, params, setParams };
}
export function useLibrary(
  tenant,
  { collection = "bots", type = "", search = "", page = 1, per_page = 25 } = {},
) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [revision, refresh] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ loading: true, data: null, error: "" });
    if (!tenant && collection !== "sources") {
      setState({ loading: false, data: null, error: "" });
      return undefined;
    }
    const query = {
      ...(type ? { type } : {}),
      ...(search ? { search } : {}),
      page,
      per_page,
    };
    (collection === "sources"
      ? axios.get("/api/source-library", { params: query }).then((r) => r.data)
      : request(tenant, "get", "library", query)
    )
      .then((data) => {
        if (active) setState({ loading: false, data, error: "" });
      })
      .catch((e) => {
        if (active)
          setState({ loading: false, data: null, error: failureText(e) });
      });
    return () => {
      active = false;
    };
  }, [tenant, collection, type, search, page, per_page, revision]);
  return { ...state, refresh: () => refresh((v) => v + 1) };
}

// WhatsApp's Bots page, where a published bot ID is connected to a number.
export function whatsappBotsUrl(apps, botId) {
  const app = (Array.isArray(apps) ? apps : []).find((item) => item?.key === "whatsapp");
  let url;
  try {
    url = new URL("/bots", app?.frontend_url || "https://whatsapps.autoappy.com");
  } catch {
    url = new URL("https://whatsapps.autoappy.com/bots");
  }
  if (botId) url.searchParams.set("bot", botId);
  return url.toString();
}
